import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  FormEditForm,
  FormEditInitialValues,
  FormComponents,
  FormAdditionalParams,
  FormFormOptions,
  onFormFormSaved,
  IFormFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedForm, ISharedPatchForm, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchForm, patchForm, getFormUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useFields, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedForm> {}

const FormFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const fields = useFields();

  useEffect(() => {
    fetchForm(id).then(form => setValue(form));
  }, [id, setValue, ]);

  const param_props: IFormFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    fields: fields.fields,
  };
  const formLayout: IForm | undefined = useMemo(() => value && FormEditForm(value, param_props, t), [value, organizations, fields, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && FormFormOptions(value, param_props, t), [value, organizations, fields, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchForm, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchForm) => value && save(() => patchForm(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onFormFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !fields.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.Form, value)}
    >
      <AtlasForm<ISharedPatchForm>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={FormComponents}
        additionalParams={FormAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={FormEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('FORM')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const FormFormEditView = React.memo(withEdit(FormFormEdit, getFormUpdateSchema));
